import { useCallback, useEffect, useState } from "react";
import { Download, Trophy } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, rupiah } from "@/lib/api";
import { downloadCsv } from "@/lib/csv";

const SORTS = [["visits", "Kunjungan terbanyak"], ["spend", "Total belanja terbesar"], ["biggest", "Transaksi terbesar"], ["recent", "Terbaru"]];
const fmt = (s) => (s ? new Date(s).toLocaleDateString("id-ID", { timeZone: "Asia/Jakarta", day: "numeric", month: "short", year: "numeric" }) : "–");
const medal = ["bg-amber-400 text-ink", "bg-slate-300 text-ink", "bg-orange-300 text-ink"];

export const CustomersPanel = () => {
  const [sort, setSort] = useState("visits");
  const [consent, setConsent] = useState(false);
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    api.get("/admin/customers", { params: { sort, consent } }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [sort, consent]);
  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    const header = ["Peringkat", "Nama", "WhatsApp", "Email", "Status", "Setuju Promo", "Kunjungan", "Pesanan Lunas", "Total Belanja", "Transaksi Terbesar", "Pesanan Pertama", "Pesanan Terakhir"];
    const rows = data.rows.map((r) => [r.rank, r.name, `+${r.whatsapp}`, r.email, r.member ? "Member" : "Tamu", r.promo_consent ? "Ya" : "Tidak",
      r.visits, r.paid_orders, r.total_spend, r.biggest_order, fmt(r.first_order_at), fmt(r.last_order_at)]);
    downloadCsv(`pelanggan-uplink-${sort}${consent ? "-setuju-promo" : ""}.csv`, header, rows);
  };

  return (
    <div data-testid="customers-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-serif font-semibold text-2xl">Pelanggan</h2>
          <p className="text-sm text-muted2">Data nama, WhatsApp & email dari setiap pesanan. Hubungi untuk promo hanya yang menyetujui.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select value={sort} onChange={(e) => setSort(e.target.value)} className="px-3 py-2 rounded-lg border border-sage-200 bg-paper text-sm" data-testid="customers-sort-select">
            {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <label className="flex items-center gap-1.5 text-sm"><input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="customers-consent-filter" /> Hanya setuju promo</label>
          <button onClick={exportCsv} disabled={!data?.rows.length} className="btn-forest px-4 py-2 text-sm flex items-center gap-2" data-testid="customers-download-button"><Download className="w-4 h-4" /> Unduh CSV</button>
        </div>
      </div>
      {data && <p className="text-xs text-muted2 mt-3" data-testid="customers-count">{data.total} pelanggan · {data.consented} setuju menerima promo</p>}
      {data && data.rows.length === 0 && <p className="text-center text-muted2 py-12" data-testid="customers-empty">Belum ada data pelanggan.</p>}
      {data?.rows.length > 0 && (
        <div className="card-paper mt-3 overflow-x-auto">
          <table className="w-full text-sm" data-testid="customers-table">
            <thead><tr className="text-left text-xs text-muted2 border-b border-sage-200">
              {["#", "Pelanggan", "WhatsApp", "Status", "Kunjungan", "Total belanja", "Transaksi terbesar", "Terakhir"].map((h) => <th key={h} className="px-3 py-2.5 font-semibold whitespace-nowrap">{h}</th>)}
            </tr></thead>
            <tbody className="divide-y divide-sage-200">
              {data.rows.map((r) => (
                <tr key={r.whatsapp} data-testid={`customer-row-${r.whatsapp}`}>
                  <td className="px-3 py-2.5"><span className={`w-7 h-7 rounded-full grid place-items-center text-xs font-bold ${medal[r.rank - 1] || "bg-sage-100"}`}>{r.rank <= 3 ? <span className="flex items-center gap-0.5"><Trophy className="w-3 h-3" />{r.rank}</span> : r.rank}</span></td>
                  <td className="px-3 py-2.5 min-w-[160px]"><div className="font-semibold">{r.name}</div><div className="text-xs text-muted2 break-all">{r.email || "–"}</div></td>
                  <td className="px-3 py-2.5 whitespace-nowrap"><a href={`https://wa.me/${r.whatsapp}`} target="_blank" rel="noreferrer" className="text-forest underline">+{r.whatsapp}</a></td>
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${r.member ? "bg-sage-100 text-forest" : "bg-paper border border-sage-200"}`}>{r.member ? "Member" : "Tamu"}</span>
                    {r.promo_consent && <span className="ml-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">Promo OK</span>}
                  </td>
                  <td className="px-3 py-2.5" data-testid={`customer-visits-${r.whatsapp}`}>{r.visits}x</td>
                  <td className="px-3 py-2.5 whitespace-nowrap">{rupiah(r.total_spend)}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap">{rupiah(r.biggest_order)}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap text-muted2">{fmt(r.last_order_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-[11px] text-muted2 mt-3">Kunjungan = jumlah hari berbeda dengan pesanan lunas. Total & transaksi terbesar hanya dari pesanan lunas.</p>
    </div>
  );
};
